import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "../../axios";
import { toast } from "react-toastify";
import ShareIcon from "@mui/icons-material/Share";
import PrintIcon from "@mui/icons-material/Print";
import DownloadIcon from "@mui/icons-material/Download";
import ReplyIcon from "@mui/icons-material/Reply";
import EditIcon from "@mui/icons-material/Edit";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import CancelIcon from "@mui/icons-material/Cancel";
import DescriptionIcon from "@mui/icons-material/Description";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import RefreshIcon from "@mui/icons-material/Refresh";
import EqualizerIcon from "@mui/icons-material/Equalizer";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";

import {
  Typography,
  Box,
  Chip,
  IconButton,
  Tooltip,
} from "@mui/material";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";

import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import dayjs from "dayjs";

import CompanyHeader from '../../components/CompanyHeader';
import {
  ReportPage, ReportFilters, ReportSelect, ReportTableCard,
  useReportDateRange, reportGridSx,
} from '../../components/Reports/ReportKit';
import { getAllowedBranchIds } from '../../utils/tokenUtils';
import InvoiceExportDialog from '../../components/PrintDesigner/InvoiceExportDialog';

export default function AllInvoicesReport() {
  const decimalPlaces = 2;

  // رقم الشركة
  const storedCompanyId = localStorage.getItem("companyId");
  const companyId = storedCompanyId ? parseInt(storedCompanyId, 10) : null;
  const [companyInfo, setCompanyInfo] = useState(null);
  const defaultBranchId = parseInt(localStorage.getItem("branchId"), 10);

  // invoiceTypeId من URL
  const [searchParams] = useSearchParams();
  const invoiceTypeIdParam = searchParams.get("invoiceTypeId") || "";
  const invoiceTypeId = invoiceTypeIdParam ? Number(invoiceTypeIdParam) : null;

  const statusParam = searchParams.get("invoiceStatus");
  const [statusFilter, setStatusFilter] = useState(statusParam ?? "");
  const [InvtypeidFilter, setinvtypidFilter] = useState(invoiceTypeIdParam ?? "");
  const [branchFilter, setBranchFilter] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const navigate = useNavigate();
  const [invoiceTypes, setInvoiceTypes] = useState([]);
  const [branches, setBranches] = useState([]);

  // التواريخ — اليوم افتراضيًا (موحّد لكل التقارير)
  const range = useReportDateRange("today");
  const { dateFrom, dateTo } = range;

  // البيانات
  const [allInvoices, setAllInvoices] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!companyId) return;
    
    // Fetch Company Info
    axios.get(`/Company/${companyId}`)
      .then(res => setCompanyInfo(res.data))
      .catch(err => console.error("Failed to load company info", err));

    axios
      .get("/InvoiceTypes", { params: { companyId } })
      .then((res) => setInvoiceTypes(res.data || []))
      .catch(() => {});

    // جلب الفروع وتصفيتها حسب الفروع المسموحة
    axios.get(`/Branch/by-company/${companyId}`).then(r => {
      const allowedBranchIds = getAllowedBranchIds();
      // ✅ القاعدة: إذا كانت القائمة فارغة → المستخدم له صلاحية على كل الفروع
      if (allowedBranchIds && allowedBranchIds.length > 0) {
        const filteredBranches = r.data.filter(b => allowedBranchIds.includes(b.id));
        setBranches(filteredBranches);
        if (defaultBranchId > 0 && filteredBranches.some(b => b.id === defaultBranchId)) {
          setBranchFilter(defaultBranchId.toString());
        } else if (filteredBranches.length > 0) {
          setBranchFilter(filteredBranches[0].id.toString());
        }
      } else {
        // المستخدم له صلاحية على كل الفروع → عرض الكل بدون فلترة
        setBranches(r.data);
      }
    });
  }, [companyId, defaultBranchId]);

  // جلب الفواتير
  const fetchAllInvoices = useCallback(async () => {
    if (!companyId) {
      console.error("⚠️ CompanyId not found in localStorage");
      return;
    }
    setLoading(true);
    try {
      const typeIdToSend = InvtypeidFilter !== "" ? Number(InvtypeidFilter) : invoiceTypeId;
      const params = {
        ...(typeIdToSend !== null && typeIdToSend !== undefined ? { invoiceTypeId: typeIdToSend } : {}),
        dateFrom: dateFrom + "T00:00:00",
        dateTo: dateTo + "T23:59:59",
        ...(statusFilter !== "" ? { invoiceStatus: statusFilter } : {}),
        ...(branchFilter !== "" ? { branchId: Number(branchFilter) } : {}),
      };
      const res = await axios.get(`/Invoice/Invoice-report/${companyId}`, {
        params,
      });
      setAllInvoices(res.data);
    } catch (error) {
      console.error("❌ خطأ في جلب تقرير الفواتير:", error);
      toast.error("فشل في تحميل تقرير الفواتير");
    } finally {
      setLoading(false);
    }
  }, [companyId, dateFrom, dateTo, invoiceTypeId, statusFilter, InvtypeidFilter, branchFilter]);

  useEffect(() => {
    fetchAllInvoices();
  }, [fetchAllInvoices]);

  // تنزيل / مشاركة PDF: نافذة اختيار القالب (تبدأ بالقالب الافتراضي ★ المحدد في مصمم الطباعة)
  const [exportTarget, setExportTarget] = useState(null); // { id, action: 'pdf' | 'share' }
  const closeExport = useCallback(() => setExportTarget(null), []);

  // تصفية البيانات بناءً على البحث
  const filteredInvoices = useMemo(() => {
    if (!searchTerm) return allInvoices;
    return allInvoices.filter(
      (inv) =>
        inv.invoiceNumber?.toString().includes(searchTerm) ||
        inv.partyName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        inv.invoiceTypeName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        inv.paymentMethodName?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [allInvoices, searchTerm]);

  // الصفوف
  const mappedRows = useMemo(() => {
    return filteredInvoices.map((inv, index) => ({
      serial: index + 1,
      invGlobalID: inv.invGlobalID,
      invoiceTypeId: inv.invoiceTypeId,
      issueDate: dayjs(inv.issueDate).format("YYYY-MM-DD HH:mm"),
      invoiceNumber: inv.invoiceNumber?.toString().replace(/[٠-٩]/g, (d) => '٠١٢٣٤٥٦٧٨٩'.indexOf(d)),
      invoiceTypeName: inv.invoiceTypeName,
      partyName: inv.partyName,
      totalBeforeTax: inv.totalBeforeTax,
      discountTotal: inv.discountTotal,
      totalAfterDiscount: inv.totalAfterDiscount,
      taxTotal: inv.taxTotal,
      totalAfterTax: inv.totalAfterTax,
      paymentMethodName: inv.paymentMethodName,
      invoiceStatus: inv.invoicepaymentstatus,
    }));
  }, [filteredInvoices]);

  // المجاميع للبيانات المفلترة
  const totals = useMemo(() => {
    return filteredInvoices.reduce(
      (acc, inv) => ({
        totalBeforeTax: acc.totalBeforeTax + (Number(inv.totalBeforeTax) || 0),
        discountTotal: acc.discountTotal + (Number(inv.discountTotal) || 0),
        totalAfterDiscount: acc.totalAfterDiscount + (Number(inv.totalAfterDiscount) || 0),
        taxTotal: acc.taxTotal + (Number(inv.taxTotal) || 0),
        totalAfterTax: acc.totalAfterTax + (Number(inv.totalAfterTax) || 0),
      }),
      {
        totalBeforeTax: 0,
        discountTotal: 0,
        totalAfterDiscount: 0,
        taxTotal: 0,
        totalAfterTax: 0,
      }
    );
  }, [filteredInvoices]);

  // تنسيق العملة
  const formatCurrency = useCallback((value) => {
    if (value == null) return "0.00";
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'SAR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value).replace('SAR', '').trim();
  }, []);

  // تنسيق الأرقام
  const formatNumber = useCallback((value) => {
    if (value == null) return "0";
    return new Intl.NumberFormat('en-US').format(value);
  }, []);

  // الأعمدة
  const columns = useMemo(() => [
    { 
      field: "serial", 
      headerName: "م", 
      width: 60,
      align: 'center',
      headerAlign: 'center',
    },
    { 
      field: "issueDate", 
      headerName: "التاريخ", 
      width: 180,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        <Typography
          sx={{
            fontFamily: 'Arial, sans-serif',
            direction: 'ltr',
            unicodeBidi: 'embed',
            fontWeight: 500,
          }}
        >
          {params.value}
        </Typography>
      ),
    },
    { 
      field: "invoiceNumber", 
      headerName: "رقم الفاتورة", 
      width: 120,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        <Typography 
          sx={{ 
            fontFamily: 'Arial, sans-serif',
            direction: 'ltr',
            unicodeBidi: 'embed',
            fontWeight: 'bold',
            color: 'primary.main'
          }}
        >
          {params.value}
        </Typography>
      ),
    },
    { 
      field: "invoiceTypeName", 
      headerName: "نوع الفاتورة", 
      flex: 1, 
      minWidth: 150,
      align: 'center',
      headerAlign: 'center',
    },
    { 
      field: "partyName", 
      headerName: "العميل/المورد", 
      flex: 1, 
      minWidth: 200,
      align: 'center',
      headerAlign: 'center',
    },
    { 
      field: "totalBeforeTax", 
      headerName: "الإجمالي", 
      width: 130, 
      type: "number",
      align: 'center',
      headerAlign: 'center',
      valueFormatter: (params) => formatCurrency(params.value),
      renderCell: (params) => (
        <Typography 
          sx={{ 
            fontFamily: 'Arial, sans-serif',
            direction: 'ltr',
            unicodeBidi: 'embed',
            fontWeight: 'bold'
          }}
        >
          {formatCurrency(params.value)}
        </Typography>
      ),
    },
    { 
      field: "discountTotal", 
      headerName: "الخصم", 
      width: 130, 
      type: "number",
      align: 'center',
      headerAlign: 'center',
      valueFormatter: (params) => formatCurrency(params.value),
      renderCell: (params) => (
        <Typography 
          sx={{ 
            fontFamily: 'Arial, sans-serif',
            direction: 'ltr',
            unicodeBidi: 'embed',
            color: 'error.main',
            fontWeight: 'bold'
          }}
        >
          {formatCurrency(params.value)}
        </Typography>
      ),
    },
    { 
      field: "totalAfterDiscount", 
      headerName: "بعد الخصم", 
      width: 140, 
      type: "number",
      align: 'center',
      headerAlign: 'center',
      valueFormatter: (params) => formatCurrency(params.value),
      renderCell: (params) => (
        <Typography 
          sx={{ 
            fontFamily: 'Arial, sans-serif',
            direction: 'ltr',
            unicodeBidi: 'embed',
            fontWeight: 'bold'
          }}
        >
          {formatCurrency(params.value)}
        </Typography>
      ),
    },
    { 
      field: "taxTotal", 
      headerName: "الضريبة", 
      width: 130, 
      type: "number",
      align: 'center',
      headerAlign: 'center',
      valueFormatter: (params) => formatCurrency(params.value),
      renderCell: (params) => (
        <Typography 
          sx={{ 
            fontFamily: 'Arial, sans-serif',
            direction: 'ltr',
            unicodeBidi: 'embed',
            color: 'warning.main',
            fontWeight: 'bold'
          }}
        >
          {formatCurrency(params.value)}
        </Typography>
      ),
    },
    { 
      field: "totalAfterTax", 
      headerName: "الصافي", 
      width: 140, 
      type: "number",
      align: 'center',
      headerAlign: 'center',
      valueFormatter: (params) => formatCurrency(params.value),
      renderCell: (params) => (
        <Typography 
          sx={{ 
            fontFamily: 'Arial, sans-serif',
            direction: 'ltr',
            unicodeBidi: 'embed',
            color: 'success.main',
            fontWeight: 'bold',
            fontSize: '1.1em'
          }}
        >
          {formatCurrency(params.value)}
        </Typography>
      ),
    },
    { 
      field: "paymentMethodName", 
      headerName: "طريقة الدفع", 
      flex: 1, 
      minWidth: 150,
      align: 'center',
      headerAlign: 'center',
    },
    {
      field: "invoiceStatus",
      headerName: "الحالة",
      width: 140,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => {
        let label = "";
        let color = "default";
        let icon = <DescriptionIcon />;

        switch (params.value) {
          case 0:
            label = "مسودة";
            color = "warning";
            icon = <EditIcon />;
            break;
          case 1:
            label = "معتمدة";
            color = "success";
            icon = <DoneAllIcon />;
            break;
          case 2:
            label = "مدفوعة";
            color = "info";
            icon = <AssignmentTurnedInIcon />;
            break;
          case 3:
            label = "ملغاة";
            color = "error";
            icon = <CancelIcon />;
            break;
          default:
            label = "غير معروف";
            color = "default";
            icon = <DescriptionIcon />;
        }

        return (
          <Chip
            icon={icon}
            label={label}
            color={color}
            variant="filled"
            size="small"
            sx={{ 
              fontWeight: "bold", 
              minWidth: "100px", 
              justifyContent: "start",
              '&:hover': {
                transform: 'scale(1.05)',
                transition: 'transform 0.2s'
              }
            }}
          />
        );
      },
    },
    {
      field: "actions",
      headerName: "إجراءات",
      flex: 1,
      minWidth: 320,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => {
        const invGlobalID = params.row.invGlobalID;
        const status = params.row.invoiceStatus;

        return (
          <Box sx={{ display: "flex", gap: 0.5, justifyContent: 'center' }}>
            <Tooltip title="تعديل">
              <IconButton
                color="primary"
                size="small"
                onClick={() => window.open(`/invoices/edit/${invGlobalID}?invoiceTypeId=${params.row.invoiceTypeId ?? ""}`, "_blank")}
                sx={{
                  '&:hover': {
                    backgroundColor: 'primary.light',
                    transform: 'scale(1.1)',
                    transition: 'all 0.2s'
                  }
                }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>

            <Tooltip title="طباعة">
              <IconButton
                color="success"
                size="small"
                onClick={() => {
                    const invoiceType = params.row.invoiceTypeId || (InvtypeidFilter !== "" ? Number(InvtypeidFilter) : invoiceTypeId);
                    if (invoiceType === 11) {
                         window.open(`/invoices/printPos/${invGlobalID}`, "_blank");
                    } else {
                        window.open(`/invoices/print/${invGlobalID}`, "_blank");
                    }
                }}
                sx={{
                  '&:hover': {
                    backgroundColor: 'success.light',
                    transform: 'scale(1.1)',
                    transition: 'all 0.2s'
                  }
                }}
              >
                <PrintIcon fontSize="small" />
              </IconButton>
            </Tooltip>

            {params.row.invoiceTypeId !== 11 && (
              <Tooltip title="نسخ الفاتورة (فاتورة جديدة بنفس البيانات)">
                <IconButton
                  size="small"
                  onClick={() => {
                    const typeForCopy = params.row.invoiceTypeId || (InvtypeidFilter !== "" ? Number(InvtypeidFilter) : invoiceTypeId) || 1;
                    window.open(`/invoices/new/${typeForCopy}?copyOf=${invGlobalID}`, "_blank");
                  }}
                  sx={{
                    color: '#4F46E5',
                    '&:hover': {
                      backgroundColor: '#eef2ff',
                      transform: 'scale(1.1)',
                      transition: 'all 0.2s'
                    }
                  }}
                >
                  <ContentCopyIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}

            <Tooltip title="مشاركة PDF">
              <IconButton
                color="secondary"
                size="small"
                onClick={() => setExportTarget({ id: invGlobalID, action: "share" })}
                sx={{
                  '&:hover': {
                    backgroundColor: 'secondary.light',
                    transform: 'scale(1.1)',
                    transition: 'all 0.2s'
                  }
                }}
              >
                <ShareIcon fontSize="small" />
              </IconButton>
            </Tooltip>

            <Tooltip title="تنزيل PDF">
              <IconButton
                color="info"
                size="small"
                onClick={() => setExportTarget({ id: invGlobalID, action: "pdf" })}
                sx={{
                  '&:hover': {
                    backgroundColor: 'info.light',
                    transform: 'scale(1.1)',
                    transition: 'all 0.2s'
                  }
                }}
              >
                <DownloadIcon fontSize="small" />
              </IconButton>
            </Tooltip>

            {status === 0 && (
              <Tooltip title="اعتماد">
                <IconButton
                  color="primary"
                  size="small"
                  onClick={async () => {
                    try {
                      await axios.post(`/Invoice/approve/${invGlobalID}`);
                      toast.success("invoicesReport.invoiceApproved");
                      fetchAllInvoices();
                    } catch {
                      toast.error("❌ فشل في اعتماد الفاتورة");
                    }
                  }}
                  sx={{
                    '&:hover': {
                      backgroundColor: 'primary.light',
                      transform: 'scale(1.1)',
                      transition: 'all 0.2s'
                    }
                  }}
                >
                  <DoneAllIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}

            {status === 1 && [1, 2, 11].includes(Number(params.row.invoiceTypeId || (InvtypeidFilter !== "" ? Number(InvtypeidFilter) : invoiceTypeId))) && (
              <Tooltip title="مرتجع">
                <IconButton
                  color="warning"
                  size="small"
                  onClick={() => {
                    // نوع الفاتورة نفسها أولًا (يعمل حتى عند عرض كل الأنواع)
                    const selectedType = Number(params.row.invoiceTypeId || (InvtypeidFilter !== "" ? Number(InvtypeidFilter) : invoiceTypeId));
                    // ✅ الشراء (2) ← مرتجع مشتريات (4)، والبيع ← مرتجع مبيعات (3)
                    const returnType = Number(selectedType) === 2 ? 4 : 3;
                    const url = selectedType === 11
                      ? `/pos?typeId=3&returnOf=${invGlobalID}`
                      : `/invoices/new/${returnType}?returnOf=${invGlobalID}&returnTypeId=${returnType}`;
                    window.open(url, "_blank");
                  }}
                  sx={{
                    '&:hover': {
                      backgroundColor: 'warning.light',
                      transform: 'scale(1.1)',
                      transition: 'all 0.2s'
                    }
                  }}
                >
                  <ReplyIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}

            {invoiceTypeId === 10 && (
              <Tooltip title="توليد فاتورة">
                <IconButton
                  color="secondary"
                  size="small"
                  onClick={() =>
                    window.open(`/invoices/new/1?fromQuote=${invGlobalID}`, "_blank")
                  }
                  sx={{
                    '&:hover': {
                      backgroundColor: 'secondary.light',
                      transform: 'scale(1.1)',
                      transition: 'all 0.2s'
                    }
                  }}
                >
                  <DescriptionIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        );
      },
    },
  ], [formatCurrency, fetchAllInvoices, invoiceTypeId, InvtypeidFilter]);

  // إعادة تعيين الفلاتر
  const resetFilters = useCallback(() => {
    range.reset(); // اليوم
    setStatusFilter("");
    setinvtypidFilter("");
    setBranchFilter("");
    setSearchTerm("");
  }, []);

  // طباعة التقرير
  const handlePrint = useCallback(() => {
    window.print();
  }, []);

  const STATUS_OPTIONS = [
    { value: 0, label: "مسودة" },
    { value: 1, label: "معتمدة" },
    { value: 2, label: "مدفوعة" },
    { value: 3, label: "ملغاة" },
  ];

  const branchName = branches.find(b => String(b.id) === String(branchFilter));
  const activeFilterCount =
    (statusFilter !== "" ? 1 : 0) + (InvtypeidFilter !== "" ? 1 : 0) +
    (branchFilter !== "" ? 1 : 0) + (searchTerm ? 1 : 0);

  return (
    <>
      <ReportPage
        title="تقرير الفواتير"
        icon={<ReceiptLongIcon />}
        period={range}
        badges={branchName ? [{ label: branchName.nameAr || branchName.name, icon: <StorefrontOutlinedIcon /> }] : []}
        actions={[
          { label: "تحديث", icon: <RefreshIcon />, onClick: fetchAllInvoices, disabled: loading },
          { label: "طباعة التقرير", icon: <PrintIcon />, onClick: handlePrint, primary: true },
        ]}
        stats={[
          { label: "عدد الفواتير", value: formatNumber(filteredInvoices.length), icon: <EqualizerIcon />, color: "primary" },
          { label: "إجمالي المبالغ", value: formatCurrency(totals.totalBeforeTax), icon: <AttachMoneyIcon />, color: "info" },
          { label: "إجمالي الخصومات", value: formatCurrency(totals.discountTotal), icon: <LocalOfferIcon />, color: "warning" },
          { label: "إجمالي الضرائب", value: formatCurrency(totals.taxTotal), icon: <ReceiptLongIcon />, color: "error" },
          { label: "الصافي النهائي", value: formatCurrency(totals.totalAfterTax), icon: <AccountBalanceWalletIcon />, color: "success", highlight: true },
        ]}
        filters={
          <ReportFilters
            range={range}
            search={searchTerm}
            onSearchChange={setSearchTerm}
            searchPlaceholder="رقم الفاتورة، العميل، النوع..."
            onApply={fetchAllInvoices}
            onReset={resetFilters}
            loading={loading}
            activeCount={activeFilterCount}
          >
            <ReportSelect
              label="الفرع"
              value={branchFilter}
              onChange={setBranchFilter}
              options={branches.map(b => ({ value: String(b.id), label: b.nameAr || b.name }))}
            />
            <ReportSelect
              label="نوع الفاتورة"
              value={InvtypeidFilter}
              onChange={setinvtypidFilter}
              options={invoiceTypes.map(t => ({ value: String(t.invoiceTypeId), label: t.name }))}
            />
            <ReportSelect
              label="الحالة"
              value={statusFilter}
              onChange={setStatusFilter}
              options={STATUS_OPTIONS}
            />
          </ReportFilters>
        }
      >
        <ReportTableCard title="الفواتير" count={formatNumber(filteredInvoices.length)}>
                <DataGrid
                  rows={mappedRows}
                  columns={columns}
                  getRowId={(row) => row.invGlobalID}
                  loading={loading}
                  components={{ Toolbar: GridToolbar }}
                  initialState={{
                    pagination: {
                      paginationModel: { pageSize: 25 },
                    },
                  }}
                  pageSizeOptions={[10, 25, 50, 100]}
                  disableSelectionOnClick
                  localeText={{
                    // ترجمة النصوص للعربية
                    noRowsLabel: 'لا توجد بيانات',
                    noResultsOverlayLabel: 'لم يتم العثور على نتائج',
                    errorOverlayDefaultLabel: 'حدث خطأ',
                    toolbarColumns: 'الأعمدة',
                    toolbarFilters: 'الفلاتر',
                    toolbarDensity: 'الكثافة',
                    toolbarExport: 'تصدير',
                    columnsPanelTextFieldLabel: 'البحث في الأعمدة',
                    columnsPanelShowAllButton: 'إظهار الكل',
                    columnsPanelHideAllButton: 'إخفاء الكل',
                    filterPanelAddFilter: 'إضافة فلتر',
                    filterPanelDeleteIconLabel: 'حذف',
                    filterPanelOperators: 'العمليات',
                    filterPanelOperatorAnd: 'و',
                    filterPanelOperatorOr: 'أو',
                    filterPanelColumns: 'الأعمدة',
                    filterPanelInputLabel: 'القيمة',
                    filterPanelInputPlaceholder: 'قيمة الفلتر',
                    filterOperatorContains: 'يحتوي على',
                    filterOperatorEquals: 'يساوي',
                    filterOperatorStartsWith: 'يبدأ بـ',
                    filterOperatorEndsWith: 'ينتهي بـ',
                    filterOperatorIsEmpty: 'فارغ',
                    filterOperatorIsNotEmpty: 'غير فارغ',
                    columnMenuLabel: 'القائمة',
                    columnMenuShowColumns: 'إظهار الأعمدة',
                    columnMenuFilter: 'فلتر',
                    columnMenuHideColumn: 'إخفاء',
                    columnMenuUnsort: 'إلغاء الترتيب',
                    columnMenuSortAsc: 'ترتيب تصاعدي',
                    columnMenuSortDesc: 'ترتيب تنازلي',
                    footerRowSelected: (count) => `${formatNumber(count)} صف محدد`,
                    footerTotalRows: 'إجمالي الصفوف:',
                    footerTotalVisibleRows: (visibleCount, totalCount) =>
                      `${formatNumber(visibleCount)} من ${formatNumber(totalCount)}`,
                  }}
                  sx={reportGridSx}
                />
        </ReportTableCard>
      </ReportPage>

      <InvoiceExportDialog
        invGlobalID={exportTarget?.id}
        action={exportTarget?.action}
        onClose={closeExport}
      />

      {/* محتوى الطباعة فقط */}
        <Box
          sx={{
            display: 'none',
            '@media print': { display: 'block' },
            p: 3,
            color: '#000',
            backgroundColor: '#fff'
          }}
          dir="rtl"
        >
          {/* رأس الشركة */}
          {companyInfo && <CompanyHeader company={companyInfo} />}
          
          <Box sx={{ textAlign: 'center', mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>📑 تقرير الفواتير</Typography>
            <Typography variant="body2">
              الفترة: من {dateFrom} إلى {dateTo}
            </Typography>
            <Typography variant="body2">
              الحالة: {statusFilter === '' ? 'الكل' : (statusFilter == 0 ? 'مسودة' : statusFilter == 1 ? 'معتمدة' : statusFilter == 2 ? 'مدفوعة' : statusFilter == 3 ? 'ملغاة' : 'غير معروف')}
            </Typography>
            <Typography variant="body2">
              نوع الفاتورة: {(InvtypeidFilter === '' && (invoiceTypeId === null || invoiceTypeId === undefined)) ? 'الكل' : (invoiceTypes.find(t => Number(t.id) === Number(InvtypeidFilter !== '' ? InvtypeidFilter : invoiceTypeId))?.name ?? '—')}
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              تم الطباعة في: {dayjs().format('YYYY-MM-DD HH:mm')}
            </Typography>
          </Box>

          {/* ملخص المجاميع */}
          <Box sx={{ mb: 2 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
              <tbody>
                <tr>
                  <td style={{ padding: '6px', border: '1px solid #ccc' }}>إجمالي المبالغ</td>
                  <td style={{ padding: '6px', border: '1px solid #ccc', textAlign: 'center' }}>{formatCurrency(totals.totalBeforeTax)}</td>
                  <td style={{ padding: '6px', border: '1px solid #ccc' }}>إجمالي الخصومات</td>
                  <td style={{ padding: '6px', border: '1px solid #ccc', textAlign: 'center' }}>{formatCurrency(totals.discountTotal)}</td>
                  <td style={{ padding: '6px', border: '1px solid #ccc' }}>إجمالي الضرائب</td>
                  <td style={{ padding: '6px', border: '1px solid #ccc', textAlign: 'center' }}>{formatCurrency(totals.taxTotal)}</td>
                  <td style={{ padding: '6px', border: '1px solid #ccc', fontWeight: 'bold' }}>الصافي النهائي</td>
                  <td style={{ padding: '6px', border: '1px solid #ccc', textAlign: 'center', fontWeight: 'bold' }}>{formatCurrency(totals.totalAfterTax)}</td>
                </tr>
              </tbody>
            </table>
          </Box>

          {/* جدول الطباعة */}
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
            <thead>
              <tr style={{ background: '#f5f5f5' }}>
                <th style={{ border: '1px solid #ccc', padding: '6px' }}>م</th>
                <th style={{ border: '1px solid #ccc', padding: '6px' }}>التاريخ</th>
                <th style={{ border: '1px solid #ccc', padding: '6px' }}>رقم الفاتورة</th>
                <th style={{ border: '1px solid #ccc', padding: '6px' }}>نوع الفاتورة</th>
                <th style={{ border: '1px solid #ccc', padding: '6px' }}>العميل/المورد</th>
                <th style={{ border: '1px solid #ccc', padding: '6px' }}>الإجمالي</th>
                <th style={{ border: '1px solid #ccc', padding: '6px' }}>الخصم</th>
                <th style={{ border: '1px solid #ccc', padding: '6px' }}>بعد الخصم</th>
                <th style={{ border: '1px solid #ccc', padding: '6px' }}>الضريبة</th>
                <th style={{ border: '1px solid #ccc', padding: '6px' }}>الصافي</th>
                <th style={{ border: '1px solid #ccc', padding: '6px' }}>طريقة الدفع</th>
                <th style={{ border: '1px solid #ccc', padding: '6px' }}>الحالة</th>
              </tr>
            </thead>
            <tbody>
              {mappedRows.map((row, idx) => (
                <tr key={row.invGlobalID}>
                  <td style={{ border: '1px solid #eee', padding: '6px', textAlign: 'center' }}>{idx + 1}</td>
                  <td style={{ border: '1px solid #eee', padding: '6px', textAlign: 'center' }}>{row.issueDate}</td>
                  <td style={{ border: '1px solid #eee', padding: '6px', textAlign: 'center', direction: 'ltr', unicodeBidi: 'embed' }}>{row.invoiceNumber}</td>
                  <td style={{ border: '1px solid #eee', padding: '6px', textAlign: 'center' }}>{row.invoiceTypeName}</td>
                  <td style={{ border: '1px solid #eee', padding: '6px', textAlign: 'center' }}>{row.partyName}</td>
                  <td style={{ border: '1px solid #eee', padding: '6px', textAlign: 'center', direction: 'ltr', unicodeBidi: 'embed' }}>{formatCurrency(row.totalBeforeTax)}</td>
                  <td style={{ border: '1px solid #eee', padding: '6px', textAlign: 'center', direction: 'ltr', unicodeBidi: 'embed' }}>{formatCurrency(row.discountTotal)}</td>
                  <td style={{ border: '1px solid #eee', padding: '6px', textAlign: 'center', direction: 'ltr', unicodeBidi: 'embed' }}>{formatCurrency(row.totalAfterDiscount)}</td>
                  <td style={{ border: '1px solid #eee', padding: '6px', textAlign: 'center', direction: 'ltr', unicodeBidi: 'embed' }}>{formatCurrency(row.taxTotal)}</td>
                  <td style={{ border: '1px solid #eee', padding: '6px', textAlign: 'center', direction: 'ltr', unicodeBidi: 'embed', fontWeight: 'bold' }}>{formatCurrency(row.totalAfterTax)}</td>
                  <td style={{ border: '1px solid #eee', padding: '6px', textAlign: 'center' }}>{row.paymentMethodName}</td>
                  <td style={{ border: '1px solid #eee', padding: '6px', textAlign: 'center' }}>
                    {row.invoiceStatus === 0 ? 'مسودة' : row.invoiceStatus === 1 ? 'معتمدة' : row.invoiceStatus === 2 ? 'مدفوعة' : row.invoiceStatus === 3 ? 'ملغاة' : 'غير معروف'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Box>
    </>
  );
}